import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateMember } from '../../mutations';
import { useMemberRates, useRoles } from '../../queries';
import { tenantApi } from '../../api/tenant.api';
import { useQueryClient } from '@tanstack/react-query';
import { useCan } from '@/app/store/session.store';
import type { MemberSummary } from '../../types';
import { CatalogSelect } from '../CatalogSelect';
import { DepartmentSelect } from '../DepartmentSelect';
import { JobTitleSelect } from '../JobTitleSelect';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Input } from '@/shared/ui/atoms/Input';
import { Select } from '@/shared/ui/atoms/Select';
import { FormField } from '@/shared/ui/molecules/FormField';
import type { WorkloadStatus } from '@/shared/ui/molecules/StatusPill/status';
import '../../../tasks/ui/NewTaskForm/NewTaskForm.styl';

type Props = { member: Pick<MemberSummary, 'id' | 'displayName' | 'jobTitle' | 'departmentId' | 'memberTypeId' | 'contractTypeId' | 'role' | 'roleId' | 'status' | 'weeklyCapacityHours' | 'availabilityNote' | 'workloadStatus'>; onSaved: () => void; onCancel: () => void; allowRole?: boolean };

const WORKLOAD: WorkloadStatus[] = ['ON_TRACK', 'BALANCED', 'SUSTAINED', 'UNDER_PRESSURE', 'OVERLOADED'];

export function MemberEditForm({ member, onSaved, onCancel, allowRole = true }: Props) {
  const update = useUpdateMember();
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(member.displayName);
  const [jobTitle, setJobTitle] = useState(member.jobTitle ?? '');
  const [departmentId, setDepartmentId] = useState(member.departmentId ?? '');
  const [memberTypeId, setMemberTypeId] = useState(member.memberTypeId ?? '');
  const [contractTypeId, setContractTypeId] = useState(member.contractTypeId ?? '');
  const roles = useRoles();
  const [roleId, setRoleId] = useState(member.roleId ?? '');
  const [capacity, setCapacity] = useState(String(member.weeklyCapacityHours));
  const [note, setNote] = useState(member.availabilityNote ?? '');
  const [workload, setWorkload] = useState<WorkloadStatus>(member.workloadStatus);
  const [status, setStatus] = useState(member.status);
  const [error, setError] = useState<string | null>(null);
  // the hourly rate turns approved logged hours into a project's spend; pay data, so only members.manage sees it
  const manages = useCan('members.manage') && allowRole;
  const rates = useMemberRates(manages);
  const savedRate = rates.data?.[member.id];
  const [rate, setRate] = useState<string | null>(null);
  const rateValue = rate ?? (savedRate !== undefined ? String(savedRate) : '');
  const client = useQueryClient();

  const saveRate = async () => {
    if (!manages || rate === null || rate === (savedRate !== undefined ? String(savedRate) : '')) return;
    await tenantApi.setMemberRate(member.id, rate.trim() === '' ? null : Number(rate));
    await client.invalidateQueries({ queryKey: ['tenant', 'member-rates'] });
    await client.invalidateQueries({ queryKey: ['portfolio'] });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    update.mutate(
      { id: member.id, displayName, jobTitle, ...(allowRole && departmentId ? { departmentId } : {}), ...(allowRole && memberTypeId ? { memberTypeId } : {}), ...(allowRole && contractTypeId ? { contractTypeId } : {}), ...(allowRole ? { ...(roleId ? { roleId } : {}), status } : {}), weeklyCapacityHours: Number(capacity), availabilityNote: note, workloadStatus: workload },
      {
        onSuccess: () =>
          saveRate().then(onSaved, (err: unknown) => setError(errorMessage(err, t('people.edit.rateError')))),
        onError: (err) => setError(errorMessage(err, t('people.edit.error')))
      }
    );
  };

  return (
    <form className="f-newtask" onSubmit={submit} noValidate>
      <div className="f-newtask__row">
        <FormField label={t('people.edit.name')} htmlFor="me-name">
          <Input icon="user4" id="me-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <FormField label={t('people.edit.jobTitle')} htmlFor="me-title">
          <JobTitleSelect id="me-title" value={jobTitle} onChange={setJobTitle} />
        </FormField>
      </div>
      {allowRole ? (
        <FormField label={t('departments.label')} htmlFor="me-department">
          <DepartmentSelect id="me-department" value={departmentId} onChange={setDepartmentId} />
        </FormField>
      ) : null}
      {allowRole ? (
        <div className="f-newtask__row">
          <FormField label={t('catalogs.member-types.label')} htmlFor="me-member-type">
            <CatalogSelect kind="member-types" id="me-member-type" value={memberTypeId} onChange={setMemberTypeId} />
          </FormField>
          <FormField label={t('catalogs.contract-types.label')} htmlFor="me-contract-type">
            <CatalogSelect kind="contract-types" id="me-contract-type" value={contractTypeId} onChange={setContractTypeId} />
          </FormField>
        </div>
      ) : null}
      <div className="f-newtask__row f-newtask__row--3">
        <FormField label={t('people.edit.capacity')} htmlFor="me-cap">
          <Input icon="clock" id="me-cap" type="number" min={0} max={80} step={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} mono />
        </FormField>
        {manages ? (
          <FormField label={t('people.edit.rate')} htmlFor="me-rate" hint={t('people.edit.rateHint')}>
            <Input icon="coin-dollar" id="me-rate" type="number" min={0} step={0.5} value={rateValue} onChange={(e) => setRate(e.target.value)} placeholder="0.00" mono />
          </FormField>
        ) : null}
        <FormField label={t('people.edit.workload')} htmlFor="me-workload">
          <Select icon="hour-glass" id="me-workload" value={workload} onChange={(e) => setWorkload(e.target.value as WorkloadStatus)}>
            {WORKLOAD.map((w) => (
              <option key={w} value={w}>
                {t(`status.${w}`)}
              </option>
            ))}
          </Select>
        </FormField>
        {allowRole ? (
          <FormField label={t('people.edit.role')} htmlFor="me-role">
            <Select icon="key2" id="me-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              {roleId ? null : <option value="">{t(`roles.${member.role}`)}</option>}
              {(roles.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormField>
        ) : (
          <div />
        )}
      </div>
      <div className="f-newtask__row">
        <FormField label={t('people.edit.note')} htmlFor="me-note" hint={t('people.edit.noteHint')}>
          <Input icon="pencil" id="me-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('people.edit.notePlaceholder')} />
        </FormField>
        {allowRole ? (
          <FormField label={t('people.edit.account')} htmlFor="me-status">
            <Select icon="shield" id="me-status" value={status} onChange={(e) => setStatus(e.target.value as MemberSummary['status'])}>
              <option value="ACTIVE">{t('people.edit.active')}</option>
              <option value="DISABLED">{t('people.edit.disabled')}</option>
            </Select>
          </FormField>
        ) : null}
      </div>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="f-newtask__actions">
        <Button variant="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={update.isPending}>
          {t('people.edit.submit')}
        </Button>
      </div>
    </form>
  );
}
